import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getMeterInfo,
  getMinSlotBeats,
  layoutBarSlots,
  type BarSlotLayout,
  type MeterInfo,
  type SplitRestSide,
} from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import type { BarState, SlotState, TimeSignature } from '../types';
import { buildBeatDots, formatBeats, splitPoints } from '../utils/beatsUtils';
import BeatDots from './BeatDots';
import SilenceIcon from './icons/SilenceIcon';
import PlusIcon from './icons/PlusIcon';
import InsertSlotIcon from './icons/InsertSlotIcon';

/** The strip's geometry, in px (mirrors RN's `BAR_STRIP`). */
const BAR_STRIP = {
  height: 76,
  /** The wider gap between a grouped meter's groups (6/8 → 3 + 3). */
  groupGap: 8,
  handleWidth: 10,
  handleHeight: 36,
  /** Extra pointer area around a handle: above and below, and to the sides (kept small so a handle doesn't eat a click on the segment beside it). */
  handleSlop: 16,
  handleSlopX: 6,
  /** The split chip, centred on the selected segment's bottom edge (half of it hangs below the strip). */
  chipWidth: 36,
  chipHeight: 26,
  /** The menu the chip opens (Add slot before / after), and its gap below the chip. */
  menuWidth: 220,
  menuGap: 6,
  rulerHeight: 18,
  /** Used until the strip has been measured (and in jsdom, which never measures). */
  fallbackWidth: 400,
} as const;

const STRIP_BORDER = 1;

/** One unit's px and the group boundaries, for turning units into x. */
interface StripScale {
  cell: number;
  boundaries: number[];
}

function makeScale(width: number, units: number, meter: MeterInfo): StripScale {
  const grouped = meter.feel === 'compound' || meter.feel === 'grouped';
  const boundaries: number[] = [];
  if (grouped) {
    let at = 0;
    for (const group of meter.groups.slice(0, -1)) {
      at += group;
      if (at < units) boundaries.push(at);
    }
  }
  const cell = Math.max(0, (width - boundaries.length * BAR_STRIP.groupGap) / units);
  return { cell, boundaries };
}

/** Left edge of whatever starts at `u` (after a group gap there). */
const xStart = (s: StripScale, u: number) => u * s.cell + s.boundaries.filter(b => b <= u).length * BAR_STRIP.groupGap;
/** Right edge of whatever ends at `u` (before a group gap there). */
const xEnd = (s: StripScale, u: number) => u * s.cell + s.boundaries.filter(b => b < u).length * BAR_STRIP.groupGap;
/** Where a change at `u` is drawn: the middle of the gap, if any. */
const xAt = (s: StripScale, u: number) => (xStart(s, u) + xEnd(s, u)) / 2;

/** Beat numbers for a position in units: 2 → "3" (counting from 1), 2.5 → "3½". */
const beatAt = (units: number) => formatBeats(units + 1);

const createStyles = (theme: Theme) => ({
  // Room below the strip for the chip's lower half
  stage: {
    position: 'relative' as const,
    height: BAR_STRIP.height + BAR_STRIP.chipHeight / 2,
    userSelect: 'none' as const,
  },
  strip: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    height: BAR_STRIP.height,
    border: `${STRIP_BORDER}px solid ${theme.colors.border}`,
    borderRadius: theme.radii.lg,
    backgroundColor: theme.colors.surface,
    boxSizing: 'border-box' as const,
  },
  segment: {
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xxs,
    padding: `0 ${theme.spacing.xxs}px`,
    // Longhands, not `border` / `background`: the selected variant overrides the colours
    borderWidth: 2,
    borderStyle: 'solid' as const,
    borderColor: 'transparent',
    borderRadius: theme.radii.lg,
    boxSizing: 'border-box' as const,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    outlineOffset: -4,
  },
  segmentSelected: {
    backgroundColor: theme.colors.surfaceTint,
    borderColor: theme.colors.primary,
  },
  chord: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.bold,
    color: theme.colors.text,
  },
  splitChip: {
    position: 'absolute' as const,
    top: BAR_STRIP.height - BAR_STRIP.chipHeight / 2,
    width: BAR_STRIP.chipWidth,
    height: BAR_STRIP.chipHeight,
    padding: 0,
    border: 'none',
    borderRadius: BAR_STRIP.chipHeight / 2,
    backgroundColor: theme.colors.primary,
    color: theme.colors.textInverse,
    boxShadow: theme.shadows.popover,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
  },
  menuBackdrop: {
    position: 'fixed' as const,
    inset: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'default',
    zIndex: 3,
  },
  menu: {
    position: 'absolute' as const,
    top: BAR_STRIP.height + BAR_STRIP.chipHeight / 2 + BAR_STRIP.menuGap,
    width: BAR_STRIP.menuWidth,
    padding: theme.spacing.xxs,
    borderRadius: theme.radii.lg,
    border: `1px solid ${theme.colors.borderSecondary}`,
    backgroundColor: theme.colors.surfaceSecondary,
    boxShadow: theme.shadows.popover,
    boxSizing: 'border-box' as const,
    zIndex: 4,
  },
  menuItem: {
    width: '100%',
    minHeight: theme.sizes.hitTarget,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: `0 ${theme.spacing.md}px`,
    border: 'none',
    borderRadius: theme.radii.md,
    background: 'transparent',
    color: theme.colors.text,
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    textAlign: 'left' as const,
    cursor: 'pointer',
  },
  menuDivider: {
    height: 1,
    margin: `0 ${theme.spacing.sm}px`,
    backgroundColor: theme.colors.border,
  },
  divider: {
    position: 'absolute' as const,
    top: BAR_STRIP.height * 0.15,
    height: BAR_STRIP.height * 0.7,
    width: 0,
    borderLeft: `1px dashed ${theme.colors.borderSecondary}`,
    pointerEvents: 'none' as const,
  },
  // The handle's pointer area; the grip is drawn inside it
  handleHit: {
    position: 'absolute' as const,
    top: (BAR_STRIP.height - BAR_STRIP.handleHeight) / 2 - BAR_STRIP.handleSlop,
    width: BAR_STRIP.handleWidth + 2 * BAR_STRIP.handleSlopX,
    height: BAR_STRIP.handleHeight + 2 * BAR_STRIP.handleSlop,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'ew-resize',
    touchAction: 'none' as const,
    background: 'none',
    border: 'none',
    padding: 0,
  },
  handle: {
    width: BAR_STRIP.handleWidth,
    height: BAR_STRIP.handleHeight,
    borderRadius: BAR_STRIP.handleWidth / 2,
    backgroundColor: theme.colors.beats,
    boxShadow: theme.shadows.popover,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  handleGrip: {
    width: 1,
    height: BAR_STRIP.handleHeight / 2.5,
    backgroundColor: theme.colors.background,
  },
  ruler: {
    position: 'relative' as const,
    height: BAR_STRIP.rulerHeight,
    margin: `0 ${STRIP_BORDER}px`,
  },
  rulerMark: {
    position: 'absolute' as const,
    top: 0,
    borderLeft: `1px solid ${theme.colors.textTertiary}`,
    paddingLeft: theme.spacing.xxs,
    height: BAR_STRIP.rulerHeight,
    fontSize: theme.typography.sizes.xs,
    lineHeight: `${BAR_STRIP.rulerHeight}px`,
    color: theme.colors.textSecondary,
  },
  rulerHalf: {
    position: 'absolute' as const,
    top: 0,
    borderLeft: `1px solid ${theme.colors.borderSecondary}`,
    height: BAR_STRIP.rulerHeight / 2,
  },
  rulerEnd: {
    position: 'absolute' as const,
    top: 0,
    borderLeft: `1px solid ${theme.colors.textTertiary}`,
    height: BAR_STRIP.rulerHeight / 2,
  },
});

/** The slot's chord as named in labels: its chord, or "rest". */
const nameOf = (label: string) => label || 'rest';

interface BoundaryHandleProps {
  x: number;
  /** Units where the change sits now, and the range it may move in. */
  at: number;
  min: number;
  max: number;
  step: number;
  /** The x of a position in units, for snapping a drag. */
  xOf: (units: number) => number;
  label: string;
  styles: ReturnType<typeof createStyles>;
  onPreview: (at: number | null) => void;
  onCommit: (at: number) => void;
  testId: string;
}

/**
 * The `beats`-coloured grip on a change between two segments. Dragging snaps
 * to the meter's steps and keeps both neighbours at least the minimum; it is
 * a slider for the keyboard (← → move one step).
 */
const BoundaryHandle: React.FC<BoundaryHandleProps> = props => {
  const latest = useRef(props);
  latest.current = props;
  const drag = useRef<{ startX: number; pointerX: number; target: number } | null>(null);

  const snap = (x: number) => {
    const { min, max, step, xOf } = latest.current;
    let best = min;
    for (let u = min; u <= max + 1e-9; u += step) {
      if (Math.abs(xOf(u) - x) < Math.abs(xOf(best) - x)) best = u;
    }
    return best;
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: latest.current.xOf(latest.current.at), pointerX: e.clientX, target: latest.current.at };
    latest.current.onPreview(latest.current.at);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!drag.current) return;
    const next = snap(drag.current.startX + e.clientX - drag.current.pointerX);
    if (next !== drag.current.target) {
      drag.current.target = next;
      latest.current.onPreview(next);
    }
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    const { target } = drag.current;
    drag.current = null;
    latest.current.onPreview(null);
    if (target !== latest.current.at) latest.current.onCommit(target);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const { at, min, max, step, onCommit } = latest.current;
    const delta = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? step : e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? -step : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next = at + delta;
    if (next >= min && next <= max) onCommit(next);
  };

  return (
    <button
      type="button"
      role="slider"
      aria-label={props.label}
      aria-valuemin={props.min + 1}
      aria-valuemax={props.max + 1}
      aria-valuenow={props.at + 1}
      aria-valuetext={`beat ${beatAt(props.at)}`}
      aria-orientation="horizontal"
      style={{ ...props.styles.handleHit, left: props.x - BAR_STRIP.handleWidth / 2 - BAR_STRIP.handleSlopX }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      onClick={e => e.stopPropagation()}
      data-testid={props.testId}
    >
      <span style={props.styles.handle} aria-hidden="true">
        <span style={props.styles.handleGrip} />
        <span style={props.styles.handleGrip} />
      </span>
    </button>
  );
};

export interface BarStripProps {
  bar: BarState;
  meter: TimeSignature;
  selectedSlotId: string | null;
  /** The slot's chord as shown, or '' for a rest. */
  labelFor: (slot: SlotState) => string;
  onSelect: (slotId: string) => void;
  /** Split the slot at a position in units from the bar's start, the new empty slot on that side of it. */
  onSplit: (slotId: string, atUnits: number, restSide: SplitRestSide) => void;
  /** Move the change after `leftSlotId` so that slot lasts `lengthUnits`. */
  onMoveBoundary: (leftSlotId: string, lengthUnits: number) => void;
}

/**
 * The bar as one horizontal track: a segment per slot over a beat grid in
 * the row's meter (cells = denominator units, grouped like the beat dots),
 * a `+` chip on the selected segment's bottom edge (a menu: an empty slot
 * before or after it) and a draggable handle on every change. All edits go back to the
 * parent, which runs them through `barEditing.ts`. Mirrors RN's BarStrip.
 */
const BarStrip: React.FC<BarStripProps> = ({ bar, meter, selectedSlotId, labelFor, onSelect, onSplit, onMoveBoundary }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const stageRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(0);
  // clientWidth, not getBoundingClientRect: the card opens inside a scale()
  // transition, and a rect taken mid-transition would stick
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setMeasured(el.clientWidth - 2 * STRIP_BORDER);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const width = measured > 0 ? measured : BAR_STRIP.fallbackWidth;

  // While a handle is dragged: which change, and where it would land
  const [drag, setDrag] = useState<{ index: number; at: number } | null>(null);

  const meterInfo = useMemo(() => getMeterInfo(meter), [meter]);
  const min = getMinSlotBeats(meter);
  const baseLayout = useMemo(() => layoutBarSlots(bar, meter), [bar, meter]);
  const layout: BarSlotLayout[] = useMemo(() => {
    if (!drag) return baseLayout;
    const left = baseLayout[drag.index];
    const right = baseLayout[drag.index + 1];
    const end = right.startUnits + right.lengthUnits;
    return baseLayout.map((l, i) =>
      i === drag.index
        ? { ...l, lengthUnits: drag.at - left.startUnits }
        : i === drag.index + 1
          ? { ...l, startUnits: drag.at, lengthUnits: end - drag.at }
          : l,
    );
  }, [baseLayout, drag]);
  const units = layout.reduce((sum, l) => sum + l.lengthUnits, 0);
  const scale = useMemo(() => makeScale(width, units, meterInfo), [width, units, meterInfo]);
  const xOf = (u: number) => xAt(scale, u);

  const selectedIndex = bar.slots.findIndex(s => s.id === selectedSlotId);
  const selected = selectedIndex === -1 ? null : layout[selectedIndex];

  // The `+` chip on the selected segment, while it can still be split
  const splits = selected && !drag ? splitPoints(selected, min) : null;
  const selectedSlot = selectedIndex === -1 ? null : bar.slots[selectedIndex];
  const selectedName = selectedSlot ? nameOf(labelFor(selectedSlot)) : '';
  const chipX = selected
    ? STRIP_BORDER + (xStart(scale, selected.startUnits) + xEnd(scale, selected.startUnits + selected.lengthUnits)) / 2
    : 0;

  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [selectedSlotId, bar]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [menuOpen]);

  // Ruler: a numbered mark per unit (only on group starts in long bars), a
  // half mark between units where halves are allowed
  const labelEvery = units > 8 && scale.boundaries.length > 0;
  const groupStarts = new Set([0, ...scale.boundaries]);

  return (
    <div>
      <div ref={stageRef} style={styles.stage} data-testid="bar-strip-stage">
        <div style={styles.strip} data-testid="bar-strip">
          {bar.slots.map((slot, i) => {
            const l = layout[i];
            const left = xStart(scale, l.startUnits);
            const right = xEnd(scale, l.startUnits + l.lengthUnits);
            const isSelected = slot.id === selectedSlotId;
            const label = labelFor(slot);
            const select = () => onSelect(slot.id);
            return (
              <div
                key={slot.id}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={`${nameOf(label)}, from beat ${beatAt(l.startUnits)}. Beats: ${formatBeats(l.lengthUnits)}`}
                onClick={select}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    select();
                  }
                }}
                style={{
                  ...styles.segment,
                  left: left - STRIP_BORDER,
                  width: right - left,
                  ...(isSelected ? styles.segmentSelected : {}),
                }}
                data-testid={`bar-segment-${slot.id}`}
              >
                {label ? (
                  <span style={styles.chord}>{label}</span>
                ) : (
                  <SilenceIcon size={theme.sizes.icon.lg} color={theme.colors.textSecondary} />
                )}
                <BeatDots dots={buildBeatDots(l, meter)} lengthUnits={l.lengthUnits} />
              </div>
            );
          })}
        </div>

        {layout.slice(0, -1).map((l, i) => (
          <div
            key={`divider-${bar.slots[i].id}`}
            style={{ ...styles.divider, left: STRIP_BORDER + xOf(l.startUnits + l.lengthUnits) }}
            aria-hidden="true"
          />
        ))}

        {baseLayout.slice(0, -1).map((l, i) => {
          const at = l.startUnits + l.lengthUnits;
          const right = baseLayout[i + 1];
          const shownAt = drag?.index === i ? drag.at : at;
          const leftSlot = bar.slots[i];
          const rightSlot = bar.slots[i + 1];
          return (
            <BoundaryHandle
              key={`handle-${leftSlot.id}`}
              x={STRIP_BORDER + xOf(shownAt)}
              at={at}
              min={l.startUnits + min}
              max={right.startUnits + right.lengthUnits - min}
              step={min}
              xOf={xOf}
              label={`Change from ${nameOf(labelFor(leftSlot))} to ${nameOf(labelFor(rightSlot))}`}
              styles={styles}
              onPreview={next => setDrag(next === null ? null : { index: i, at: next })}
              onCommit={next => onMoveBoundary(leftSlot.id, next - l.startUnits)}
              testId={`bar-handle-${leftSlot.id}`}
            />
          );
        })}

        {splits && selectedSlot && (
          <button
            type="button"
            aria-label={`Split ${selectedName}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(open => !open)}
            style={{ ...styles.splitChip, left: chipX - BAR_STRIP.chipWidth / 2 }}
            data-testid="bar-split-chip"
          >
            <PlusIcon size={theme.sizes.icon.sm} color="currentColor" />
          </button>
        )}

        {menuOpen && splits && selectedSlot && (
          <>
            <button
              type="button"
              aria-label="Close"
              tabIndex={-1}
              onClick={() => setMenuOpen(false)}
              style={styles.menuBackdrop}
              data-testid="bar-split-backdrop"
            />
            <div
              role="menu"
              style={{
                ...styles.menu,
                left: Math.min(Math.max(chipX - BAR_STRIP.menuWidth / 2, 0), width + 2 * STRIP_BORDER - BAR_STRIP.menuWidth),
              }}
            >
              {splits.map(({ side, at }, i) => (
                <React.Fragment key={side}>
                  {i > 0 && <div style={styles.menuDivider} />}
                  <button
                    type="button"
                    role="menuitem"
                    aria-label={`Split ${selectedName}: an empty slot ${side} it, from beat ${beatAt(at)}`}
                    onClick={() => {
                      setMenuOpen(false);
                      onSplit(selectedSlot.id, at, side);
                    }}
                    style={styles.menuItem}
                    data-testid={`bar-split-${side}`}
                  >
                    <InsertSlotIcon position={side} size={theme.sizes.icon.md} color={theme.colors.primary} />
                    {side === 'before' ? `Add slot before ${selectedName}` : `Add slot after ${selectedName}`}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={styles.ruler} aria-hidden="true">
        {Array.from({ length: Math.ceil(units) }, (_, k) => (
          <React.Fragment key={k}>
            <div style={{ ...styles.rulerMark, left: xStart(scale, k) }}>{!labelEvery || groupStarts.has(k) ? k + 1 : ''}</div>
            {min < 1 && <div style={{ ...styles.rulerHalf, left: xStart(scale, k + 0.5) }} />}
          </React.Fragment>
        ))}
        <div style={{ ...styles.rulerEnd, left: xEnd(scale, units) - 1 }} />
      </div>
    </div>
  );
};

export default BarStrip;
